// Build an iCalendar (.ics) blob for a football fixture and trigger
// a browser download. The user picks their own calendar from the OS
// prompt — Google / Apple / Outlook all consume the same format, so
// one helper covers everything.
//
// The .ics is generated entirely client-side; no server round-trip.

// Format a Date as the compact UTC stamp .ics wants:
//   20260518T193000Z
function icsDate(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return (
    d.getUTCFullYear().toString()
    + pad(d.getUTCMonth() + 1)
    + pad(d.getUTCDate())
    + 'T'
    + pad(d.getUTCHours())
    + pad(d.getUTCMinutes())
    + pad(d.getUTCSeconds())
    + 'Z'
  )
}

// RFC 5545 line escaping. Commas, semicolons and backslashes need
// backslash-escaping; CRLF inside content becomes \n.
function escapeIcsText(s) {
  if (!s) return ''
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
}

// `fixture` shape from sports_fixtures (list view) OR football-data's
// /matches/{id} response — we read the fields that are in both:
//   { api_id, home_team, away_team, kickoff_at OR utcDate, venue,
//     league.name OR competition.name, round OR matchday }
//
// Duration defaults to 2 hours, which is enough for 90' + injury +
// the half-time break. Penalty shootouts overflow but adding extra
// padding causes more pain than it saves (calendars hate stale busy
// blocks).
export function downloadFixtureIcs(fixture) {
  if (!fixture) return
  const kickoffIso = fixture.kickoff_at || fixture.utcDate
  if (!kickoffIso) return
  const start = new Date(kickoffIso)
  const end = new Date(start.getTime() + 2 * 3600 * 1000)
  const id = fixture.api_id || fixture.id || `${start.getTime()}`

  const home = fixture.home_team || fixture.homeTeam?.name || fixture.homeTeam?.shortName || 'Home'
  const away = fixture.away_team || fixture.awayTeam?.name || fixture.awayTeam?.shortName || 'Away'
  const summary = `${home} vs ${away}`

  const leagueName = fixture.league?.name || fixture.competition?.name
  const round = fixture.round || (fixture.matchday ? `Matchday ${fixture.matchday}` : null)
  const descParts = []
  if (leagueName) descParts.push(leagueName)
  if (round) descParts.push(round)

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Phoenix Tracker//Sports//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${id}@phoenix-tracker`,
    `DTSTAMP:${icsDate(new Date())}`,
    `DTSTART:${icsDate(start)}`,
    `DTEND:${icsDate(end)}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    fixture.venue ? `LOCATION:${escapeIcsText(fixture.venue)}` : null,
    descParts.length ? `DESCRIPTION:${escapeIcsText(descParts.join(' — '))}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean)

  // \r\n line endings are required by the spec — some parsers
  // accept LF-only but the strict ones (older Outlook) won't.
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const filenameTeam = (home + '-' + away).replace(/[^a-z0-9-]+/gi, '-').replace(/-+/g, '-')
  a.download = `match-${filenameTeam}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke after the click registers so older browsers don't choke.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
